<template>
    <div class="min-h-screen bg-gradient-to-br from-gray-100 to-blue-50">
        <div class="container mx-auto px-4 py-8">

            <!-- Breadcrumb -->
            <nav class="text-sm text-gray-600 mb-6 flex pt-6 sm:pt-0 pl-4 sm:pl-0">
                <router-link to="/dashboard" class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
                <span class="mx-1">/</span>
                <span class="text-blue-900 font-semibold">Student Life</span>
            </nav>

            <!-- Header Section -->
            <div class="bg-white rounded-3xl shadow-md border border-gray-200 p-8 mb-8">
                <div class="flex items-center justify-between">
                    <div>
                        <h1 class="text-xl sm:text-3xl font-bold text-blue-900 flex items-center gap-3">Student Life
                        </h1>
                        <p class="text-gray-500 mt-2 text-xs sm:text-sm">Discover and manage student clubs, sports, and
                            other activities</p>
                    </div>
                    <div v-if="hasPermission('extracurricular-activities-create')"
                        class="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all duration-200 text-white cursor-pointer rounded-lg"
                        @click="openCreate">
                        <font-awesome-icon icon="plus" class="text-lg" /> Create
                    </div>
                </div>
            </div>

            <!-- Analytics Dashboard -->
            <div v-if="analyticsData" class="mb-8">
                <!-- Summary Cards -->
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <!-- Total Activities Card -->
                    <div
                        class="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-sm font-medium text-gray-600 mb-1">Total Clubs</p>
                                <p class="text-3xl font-bold text-blue-600">{{ analyticsData.totalActivities }}</p>
                            </div>
                            <div
                                class="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                                <font-awesome-icon icon="building" class="text-white text-xl" />
                            </div>
                        </div>
                    </div>

                    <!-- Total Participants Card -->
                    <div
                        class="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-sm font-medium text-gray-600 mb-1">Total Participants</p>
                                <p class="text-3xl font-bold text-purple-600">{{ analyticsData.totalParticipants }}</p>
                            </div>
                            <div
                                class="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                                <font-awesome-icon icon="user-friends" class="text-white text-xl" />
                            </div>
                        </div>
                    </div>

                    <!-- Accepted Participants Card -->
                    <div
                        class="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-sm font-medium text-gray-600 mb-1">Accepted</p>
                                <p class="text-3xl font-bold text-teal-600">{{ analyticsData.acceptedParticipants }}
                                </p>
                            </div>
                            <div
                                class="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                                <font-awesome-icon icon="check-circle" class="text-white text-xl" />
                            </div>
                        </div>
                    </div>

                    <!-- Pending Participants Card -->
                    <div
                        class="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-sm font-medium text-gray-600 mb-1">Pending</p>
                                <p class="text-3xl font-bold text-yellow-600">{{ analyticsData.pendingParticipants }}
                                </p>
                            </div>
                            <div
                                class="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center">
                                <font-awesome-icon icon="clock" class="text-white text-xl" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Charts Section -->
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    <!-- Bar Chart for Accepted Students by Activity -->
                    <div class="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                        <div class="flex items-center justify-between mb-6">
                            <h3 class="text-lg font-semibold text-gray-900">Club Participation Overview</h3>
                            <div
                                class="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                                <font-awesome-icon icon="chart-bar" class="text-white text-sm" />
                            </div>
                        </div>
                        <div v-if="analyticsData && analyticsData.activitiesWithParticipants && analyticsData.activitiesWithParticipants.length > 0"
                            class="h-80">
                            <apexchart type="bar" height="320" :options="chartOptions" :series="chartSeries" />
                        </div>
                        <div v-else class="h-80 flex items-center justify-center text-gray-500">
                            <div class="text-center">
                                <font-awesome-icon icon="chart-bar" class="text-4xl mb-2" />
                                <p>No data available for chart</p>
                            </div>
                        </div>
                    </div>

                    <!-- Status Distribution Pie Chart -->
                    <div class="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                        <div class="flex items-center justify-between mb-6">
                            <h3 class="text-lg font-semibold text-gray-900">Participation Status Distribution</h3>
                            <div
                                class="w-8 h-8 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg flex items-center justify-center">
                                <font-awesome-icon icon="chart-pie" class="text-white text-sm" />
                            </div>
                        </div>
                        <div v-if="analyticsData && analyticsData.totalParticipants > 0" class="h-80">
                            <apexchart type="donut" height="320" :options="pieChartOptions" :series="pieChartSeries" />
                        </div>
                        <div v-else class="h-80 flex items-center justify-center text-gray-500">
                            <div class="text-center">
                                <font-awesome-icon icon="chart-pie" class="text-4xl mb-2" />
                                <p>No data available for chart</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Top Users Chart -->
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div class="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                        <div class="flex items-center justify-between mb-6">
                            <h3 class="text-lg font-semibold text-gray-900">Top 5 Club Creators</h3>
                            <div
                                class="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                                <font-awesome-icon icon="trophy" class="text-white text-sm" />
                            </div>
                        </div>
                        <div v-if="analyticsData && analyticsData.topUsersByClubs && analyticsData.topUsersByClubs.length > 0"
                            class="h-80">
                            <apexchart type="bar" height="320" :options="topUsersChartOptions"
                                :series="topUsersChartSeries" />
                        </div>
                        <div v-else class="h-80 flex items-center justify-center text-gray-500">
                            <div class="text-center">
                                <font-awesome-icon icon="trophy" class="text-4xl mb-2" />
                                <p>No data available for chart</p>
                            </div>
                        </div>
                    </div>

                    <!-- Top Clubs by Posts Chart -->
                    <div class="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                        <div class="flex items-center justify-between mb-6">
                            <h3 class="text-lg font-semibold text-gray-900">Top 5 Most Active Clubs</h3>
                            <div
                                class="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                                <font-awesome-icon icon="comments" class="text-white text-sm" />
                            </div>
                        </div>
                        <div v-if="analyticsData && analyticsData.topClubsByPosts && analyticsData.topClubsByPosts.length > 0"
                            class="h-80">
                            <apexchart type="bar" height="320" :options="topClubsPostsChartOptions"
                                :series="topClubsPostsChartSeries" />
                        </div>
                        <div v-else class="h-80 flex items-center justify-center text-gray-500">
                            <div class="text-center">
                                <font-awesome-icon icon="comments" class="text-4xl mb-2" />
                                <p>No data available for chart</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-show="items.length === 0 && !isLoading" class="flex flex-col items-center justify-center py-16">
                <div
                    class="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mb-6">
                    <font-awesome-icon icon="users" class="text-3xl text-blue-600" />
                </div>
                <h3 class="text-xl font-semibold text-gray-900 mb-2">No Activities Yet</h3>
                <p class="text-gray-600 text-center mb-6 max-w-md">Start building your community by creating the first
                    club.</p>
                <base-button mode="primary" class="flex items-center gap-2" @click="openCreate">
                    <font-awesome-icon icon="plus" /> Create First Club
                </base-button>
            </div>

            <!-- Activities Grid -->
            <div v-show="items.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <div v-for="item in items" :key="item.token"
                    class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group flex flex-col cursor-pointer"
                    @click.stop="goToActivityDetail(item.token)">
                    <div class="h-48 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden relative">
                        <img :src="getThumbnailUrl(item.thumbnail_url)" alt="thumbnail"
                            class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy" @error="handleThumbnailError" />
                    </div>
                    <div class="p-6 flex-1 flex flex-col">
                        <h3
                            class="text-lg font-semibold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors duration-200">
                            {{ item.title }}</h3>
                        <p class="text-gray-600 text-sm line-clamp-3 mb-4 leading-relaxed">{{ item.description }}</p>

                        <!-- Participant Stats -->
                        <div class="flex justify-between items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                            <div class="flex items-center gap-2">
                                <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <font-awesome-icon icon="users" class="text-blue-600 text-sm" />
                                </div>
                                <div>
                                    <p class="text-xs text-gray-500 font-medium">Total</p>
                                    <p class="text-xs font-semibold text-gray-900 text-center">{{
                                        item.participants_count || 0 }}
                                    </p>
                                </div>
                            </div>
                            <div class="flex items-center gap-2">
                                <div class="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                                    <font-awesome-icon icon="clock" class="text-yellow-600 text-sm" />
                                </div>
                                <div>
                                    <p class="text-xs text-gray-500 font-medium">Pending</p>
                                    <p class="text-xs font-semibold text-gray-900 text-center">{{
                                        item.pending_participants_count ||
                                        0 }}</p>
                                </div>
                            </div>
                        </div>

                        <div class="flex-1 flex flex-col justify-end">
                            <!-- Join Button for Students -->
                            <div v-if="hasPermission('extracurricular-activities-register') && !canManage(item)"
                                class="mb-4">
                                <!-- Pending Status -->
                                <div v-if="item.participantStatus === 'pending'"
                                    class="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 shadow-md">
                                    <font-awesome-icon icon="clock" />
                                    Pending Approval
                                </div>
                                <!-- Approved Status -->
                                <div v-else-if="item.participantStatus === 'approved'"
                                    class="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 shadow-md">
                                    <font-awesome-icon icon="check-circle" />
                                    Approved
                                </div>
                                <!-- Rejected Status -->
                                <div v-else-if="item.participantStatus === 'rejected'"
                                    class="w-full bg-gradient-to-r from-red-500 to-pink-600 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 shadow-md">
                                    <font-awesome-icon icon="times-circle" />
                                    Rejected
                                </div>
                                <!-- Join Button (Not registered yet) -->
                                <button v-else
                                    class="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                    @click.stop="registerToActivity(item)" :disabled="registeringItems[item.token]">
                                    <font-awesome-icon v-if="registeringItems[item.token]" icon="spinner"
                                        class="animate-spin" />
                                    <font-awesome-icon v-else icon="user-plus" />
                                    {{ registeringItems[item.token] ? 'Joining...' : 'Join Club' }}
                                </button>
                            </div>

                            <div class="flex items-center justify-between pt-4 border-t border-gray-100">
                                <div @click.stop="goToUserProfile(item.user.token)"
                                    :title="`Click to view ${item.user.name}'s profile`"
                                    class="flex items-center gap-3 cursor-pointer">
                                    <div
                                        class="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                                        <img :src="getProfilePicture(item.user)"
                                            v-if="item.user && item.user.profile_picture_url"
                                            class="w-full h-full object-cover" @error="handleImageError" />
                                        <font-awesome-icon v-else icon="user" class="text-white text-sm" />
                                    </div>
                                    <div>
                                        <p
                                            class="text-sm font-medium text-gray-900 truncate-1 hover:text-blue-600 transition-colors">
                                            {{ item.user &&
                                                item.user.name }}
                                        </p>
                                        <p class="text-xs text-gray-500">
                                            {{
                                                formatDate(item.created_at) }}</p>
                                    </div>
                                </div>
                                <div class="flex items-center gap-2" v-if="canManage(item)">
                                    <button
                                        class="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all duration-200"
                                        @click.stop="openEdit(item)" title="Edit">
                                        <font-awesome-icon icon="pen" class="text-sm" />
                                    </button>
                                    <button
                                        class="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all duration-200"
                                        @click.stop="confirmDelete(item)" title="Delete">
                                        <font-awesome-icon icon="trash" class="text-sm" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Loading indicator -->
            <div v-if="isLoading && page > 1" class="flex justify-center py-8">
                <div class="flex items-center space-x-2 text-gray-500">
                    <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span>Loading more clubs...</span>
                </div>
            </div>

            <!-- No more activities message -->
            <div v-if="!hasMore && items.length > 0" class="text-center py-8 text-gray-500">
                <p>No more clubs to load</p>
            </div>
        </div>

        <base-modal v-if="showForm" :title="isEdit ? 'Edit Club' : 'Create Club'" @close="closeForm">
            <template #default>
                <form @submit.prevent="submit" class="px-8 pt-3">
                    <div class="space-y-4 p-1">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">Title <span
                                    class="text-red-500">*</span></label>
                            <input v-model="form.title" type="text" placeholder="Enter activity title"
                                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                required />
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">Description <span
                                    class="text-red-500">*</span></label>
                            <textarea v-model="form.description" rows="3" placeholder="Describe the activity..."
                                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                                required />
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">Thumbnail</label>
                            <div class="relative">
                                <input @change="onFileChange" type="file" accept="image/*" id="thumbnail-upload"
                                    class="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                                <label for="thumbnail-upload"
                                    class="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                                    <div class="text-center">
                                        <font-awesome-icon icon="cloud-upload-alt"
                                            class="text-2xl text-gray-400 mb-2" />
                                        <p class="text-sm text-gray-600">Click to upload thumbnail</p>
                                        <p class="text-xs text-gray-500 mt-1">JPG, PNG, WEBP up to 5MB</p>
                                    </div>
                                </label>
                            </div>
                            <div v-if="form.thumbnail" class="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                                <p class="text-sm text-green-700 flex items-center">
                                    <font-awesome-icon icon="check-circle" class="mr-2" />
                                    {{ form.thumbnail.name }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="flex justify-end gap-3 mt-6">
                        <base-button mode="secondary" @click="closeForm" class="px-6 py-2">
                            Cancel
                        </base-button>
                        <base-button mode="primary" :disabled="isSubmitting" @click="submit" class="px-6 py-2">
                            {{ isEdit ? 'Update' : 'Create' }}
                        </base-button>
                    </div>

                </form>
            </template>
        </base-modal>

        <div class="h-full flex justify-center items-center" v-if="isSubmitting">
            <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
        </div>

    </div>
</template>

<script>
import gql from 'graphql-tag';

const LIST_EXTRACURRICULAR_ACTIVITIES = gql`
  query ListExtracurricularActivities($first: Int!, $page: Int!) {
    listExtracurricularActivities(first: $first, page: $page) {
      error
      error_code
      message
      paginatorInfo { hasMorePages currentPage }
      data {
        token
        title
        description
        thumbnail_url
        participantStatus
        user { id token name profile_picture_url }
        created_at
        participants_count
        pending_participants_count
      }
    }
  }
`;

const CREATE_EXTRACURRICULAR_ACTIVITY = gql`
  mutation CreateExtracurricularActivity($input: ExtracurricularActivityInput!) {
    createExtracurricularActivity(input: $input) { error error_code message }
  }
`;

const UPDATE_EXTRACURRICULAR_ACTIVITY = gql`
  mutation UpdateExtracurricularActivity($token: String!, $input: ExtracurricularActivityInput!) {
    updateExtracurricularActivity(token: $token, input: $input) { error error_code message }
  }
`;

const DELETE_EXTRACURRICULAR_ACTIVITY = gql`
  mutation DeleteExtracurricularActivity($token: String!) {
    deleteExtracurricularActivity(token: $token) { error error_code message }
  }
`;

const REGISTER_EXTRACURRICULAR_ACTIVITY = gql`
  mutation RegisterExtracurricularActivity($token: String!) {
    registerExtracurricularActivity(token: $token) { error error_code message }
  }
`;

const GET_EXTRACURRICULAR_ACTIVITY_STATISTICS = gql`
  query GetExtracurricularActivityStatistics {
    getExtracurricularActivityStatistics {
      error
      error_code
      message
      data {
        totalActivities
        totalParticipants
        acceptedParticipants
        pendingParticipants
        rejectedParticipants
        activitiesWithParticipants {
          title
          token
          accepted_count
          pending_count
        }
        topUsersByClubs {
          user_name
          user_id
          clubs_count
        }
        topClubsByPosts {
          club_title
          club_token
          posts_count
        }
      }
    }
  }
`;

export default {
    name: 'student-life-index',
    data() {
        return {
            first: 12,
            page: 1,
            hasMore: true,
            items: [],
            isLoading: false,
            error: null,
            showForm: false,
            isEdit: false,
            selectedToken: null,
            form: { title: '', description: '', thumbnail: null },
            isSubmitting: false,
            defaultThumbnail: require('../../assets/images/default/thumbnail.jpeg'),
            registeringItems: {}, // Track loading state for each item by token
            analyticsData: null,
            analyticsLoading: false,
            chartData: [],
            chartOptions: {
                chart: {
                    type: 'bar',
                    toolbar: {
                        show: false
                    },
                    fontFamily: 'Inter, sans-serif'
                },
                colors: ['#10B981', '#F59E0B'],
                plotOptions: {
                    bar: {
                        columnWidth: '60%',
                        horizontal: false
                    }
                },
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: '12px',
                        fontWeight: 'bold'
                    }
                },
                xaxis: {
                    categories: [],
                    labels: {
                        style: {
                            fontSize: '12px',
                            fontWeight: '500'
                        },
                        rotate: -45
                    }
                },
                yaxis: {
                    title: {
                        text: 'Number of Participants',
                        style: {
                            fontSize: '14px',
                            fontWeight: '600'
                        }
                    },
                    labels: {
                        show: false
                    },
                    min: 0,
                    forceNiceScale: true,
                    decimalsInFloat: 0
                },
                legend: {
                    position: 'bottom',
                    fontSize: '14px',
                    fontWeight: '500',
                    markers: {
                        width: 12,
                        height: 12,
                        radius: 2
                    }
                },
                grid: {
                    show: false
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return val + " participants"
                        }
                    }
                }
            },
            chartSeries: [{
                name: 'Accepted Participants',
                data: []
            }],
            pieChartData: [],
            pieChartOptions: {
                chart: {
                    type: 'donut',
                    fontFamily: 'Inter, sans-serif'
                },
                colors: ['#10B981', '#F59E0B', '#EF4444'],
                labels: ['Accepted', 'Pending', 'Rejected'],
                legend: {
                    position: 'bottom',
                    fontSize: '14px',
                    fontWeight: '500',
                    formatter: function (seriesName, opts) {
                        return seriesName + ": " + opts.w.globals.series[opts.seriesIndex];
                    }
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '70%',
                            labels: {
                                show: true,
                                total: {
                                    show: true,
                                    label: 'Total',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: '#374151'
                                }
                            }
                        }
                    }
                },
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: '12px',
                        fontWeight: 'bold'
                    }
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return val + " participants"
                        }
                    }
                }
            },
            pieChartSeries: [],
            topUsersChartOptions: {
                chart: {
                    type: 'bar',
                    toolbar: {
                        show: false
                    },
                    fontFamily: 'Inter, sans-serif'
                },
                colors: ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981'],
                plotOptions: {
                    bar: {
                        columnWidth: '60%',
                        horizontal: false,
                        distributed: true,
                        barHeight: '70%',
                        maxWidth: 50
                    }
                },
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: '12px',
                        fontWeight: 'bold'
                    }
                },
                xaxis: {
                    categories: [],
                    labels: {
                        style: {
                            fontSize: '12px',
                            fontWeight: '500'
                        },
                        rotate: -45
                    }
                },
                yaxis: {
                    title: {
                        text: 'Number of Clubs',
                        style: {
                            fontSize: '14px',
                            fontWeight: '600'
                        }
                    },
                    labels: {
                        show: false
                    },
                    min: 0,
                    forceNiceScale: true,
                    decimalsInFloat: 0
                },
                grid: {
                    show: false
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return val + " clubs"
                        }
                    }
                }
            },
            topUsersChartSeries: [{
                name: 'Clubs Created',
                data: []
            }],
            topClubsPostsChartOptions: {
                chart: {
                    type: 'bar',
                    toolbar: {
                        show: false
                    },
                    fontFamily: 'Inter, sans-serif'
                },
                colors: ['#EF4444', '#F97316', '#EAB308', '#22C55E', '#06B6D4'],
                plotOptions: {
                    bar: {
                        columnWidth: '60%',
                        horizontal: false,
                        distributed: true,
                    }
                },
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: '12px',
                        fontWeight: 'bold'
                    }
                },
                xaxis: {
                    categories: [],
                    labels: {
                        style: {
                            fontSize: '12px',
                            fontWeight: '500'
                        },
                        rotate: -45
                    }
                },
                yaxis: {
                    title: {
                        text: 'Number of Posts',
                        style: {
                            fontSize: '14px',
                            fontWeight: '600'
                        }
                    },
                    labels: {
                        show: false
                    },
                    min: 0,
                    forceNiceScale: true,
                    decimalsInFloat: 0
                },
                grid: {
                    show: false
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return val + " posts"
                        }
                    }
                }
            },
            topClubsPostsChartSeries: [{
                name: 'Posts Created',
                data: []
            }]
        };
    },
    mounted() {
        // Fetch initial activities when component is mounted
        this.fetchActivities();

        // Fetch analytics data
        this.fetchAnalytics();

        // Add scroll event listener to the dashboard layout's scroll container
        this.$nextTick(() => {
            const scrollContainer = document.getElementById('dashboard-scroll-container');
            if (scrollContainer) {
                scrollContainer.addEventListener('scroll', this.handleScroll);
            } else {
                console.warn('Dashboard scroll container not found');
            }
        });
    },
    beforeUnmount() {
        // Remove scroll event listener
        const scrollContainer = document.getElementById('dashboard-scroll-container');
        if (scrollContainer) {
            scrollContainer.removeEventListener('scroll', this.handleScroll);
        }
    },
    computed: {
        role() { return this.$store.getters.role; },
        userId() { return this.$store.getters.userId; },
    },
    methods: {
        goToUserProfile(token) {
            this.$router.push(`/profile/${token}`);
        },
        hasPermission(crudName) {
            const permissions = this.$store.getters.permissions;
            const permissionsArray = permissions.split(",");
            if (permissionsArray.includes(crudName)) {
                return true;
            } else {
                return false;
            }
        },
        canManage(item) {
            return (item.user && String(item.user.id) === String(this.userId)) || this.$store.getters.isSuperAdmin;
        },
        async fetchActivities() {
            if (this.isLoading) return;

            this.isLoading = true;
            this.error = null;

            try {
                const result = await this.$apollo.query({
                    query: LIST_EXTRACURRICULAR_ACTIVITIES,
                    variables: {
                        first: this.first,
                        page: this.page
                    },
                    fetchPolicy: 'network-only'
                });

                const response = result.data && result.data.listExtracurricularActivities;

                if (!response) {
                    throw new Error('Invalid response format');
                }

                if (response.error) {
                    throw new Error(response.message || 'Failed to fetch activities');
                }

                // Update pagination info
                this.hasMore = response.paginatorInfo && response.paginatorInfo.hasMorePages;

                // Handle activities data safely
                const newActivities = response.data || [];

                // If this is the first page, replace activities
                // If this is a subsequent page, append to existing activities
                if (this.page === 1) {
                    this.items = newActivities;
                } else {
                    this.items = [...this.items, ...newActivities];
                }

            } catch (error) {
                console.error('Error fetching activities:', error);
                this.handleError(error.message || 'Failed to fetch activities');
            } finally {
                this.isLoading = false;
            }
        },

        openCreate() {
            this.isEdit = false;
            this.selectedToken = null;
            this.form = { title: '', description: '', thumbnail: null };
            this.showForm = true;
        },
        openEdit(item) {
            this.isEdit = true;
            this.selectedToken = item.token;
            this.form = { title: item.title, description: item.description, thumbnail: null };
            this.showForm = true;
        },
        closeForm() {
            this.showForm = false;
            this.isSubmitting = false;
        },
        onFileChange(e) {
            const file = e.target.files && e.target.files[0];
            this.form.thumbnail = file || null;
        },
        async submit() {
            if (!this.form.title || !this.form.description) {
                this.$swal.fire({
                    title: 'Validation',
                    text: 'Title and Description are required.',
                    icon: 'warning',
                    position: 'top-end',
                    toast: true
                });
                return;
            }
            this.isSubmitting = true;
            try {
                const variables = { input: { title: this.form.title, description: this.form.description, thumbnail: this.form.thumbnail } };
                if (this.isEdit) {
                    await this.$apollo.mutate({ mutation: UPDATE_EXTRACURRICULAR_ACTIVITY, variables: { token: this.selectedToken, ...variables } });
                } else {
                    await this.$apollo.mutate({ mutation: CREATE_EXTRACURRICULAR_ACTIVITY, variables });
                }
                this.$swal.fire({
                    title: 'Success',
                    text: `Club ${this.isEdit ? 'updated' : 'created'} successfully.`,
                    icon: 'success',
                    position: 'top-end',
                    toast: true,
                    showConfirmButton: false,
                    timer: 3000
                });
                this.closeForm();
                // reload list and analytics
                this.resetComponent();
                this.fetchActivities();
                this.fetchAnalytics();
            } catch (e) {
                this.$swal.fire({
                    title: 'Error',
                    text: 'Something went wrong. Please try again.',
                    icon: 'error',
                    position: 'top-end',
                    toast: true
                });
            } finally {
                this.isSubmitting = false;
            }
        },
        async confirmDelete(item) {
            const res = await this.$swal.fire({
                title: 'Delete Club?',
                text: 'This will remove the club from listing. You cannot undo this action.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, delete',
            });
            if (!res.isConfirmed) return;
            try {
                await this.$apollo.mutate({ mutation: DELETE_EXTRACURRICULAR_ACTIVITY, variables: { token: item.token } });
                this.$swal.fire({
                    title: 'Deleted',
                    text: 'Club deleted successfully.',
                    icon: 'success',
                    position: 'top-end',
                    toast: true,
                    showConfirmButton: false,
                    timer: 3000
                });
                this.items = this.items.filter(x => x.token !== item.token);
                // Refresh analytics after deletion
                this.fetchAnalytics();
            } catch (e) {
                this.$swal.fire({
                    title: 'Error',
                    text: 'Delete failed. Please try again.',
                    icon: 'error',
                    position: 'top-end',
                    toast: true
                });
            }
        },
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        },
        handleError(message) {
            this.error = message;
            this.$swal.fire({
                title: 'Error',
                text: message,
                icon: 'error',
                position: 'top-end',
                toast: true
            });
        },
        resetComponent() {
            this.items = [];
            this.page = 1;
            this.hasMore = true;
            this.error = null;
            this.isLoading = false;
        },
        async fetchAnalytics() {
            this.analyticsLoading = true;
            try {
                const result = await this.$apollo.query({
                    query: GET_EXTRACURRICULAR_ACTIVITY_STATISTICS,
                    fetchPolicy: 'network-only'
                });

                const response = result.data && result.data.getExtracurricularActivityStatistics;

                if (!response) {
                    throw new Error('Invalid response format');
                }

                if (response.error) {
                    throw new Error(response.message || 'Failed to fetch analytics');
                }

                this.analyticsData = response.data;
                this.prepareChartData();

            } catch (error) {
                console.error('Error fetching analytics:', error);
                this.handleError(error.message || 'Failed to fetch analytics');
            } finally {
                this.analyticsLoading = false;
            }
        },
        prepareChartData() {
            if (!this.analyticsData) return;

            // Prepare bar chart data with both accepted and pending participants
            const activitiesData = this.analyticsData.activitiesWithParticipants || [];
            this.chartData = activitiesData;

            // Update chart options with categories
            this.chartOptions = {
                ...this.chartOptions,
                xaxis: {
                    ...this.chartOptions.xaxis,
                    categories: activitiesData.map(item => item.title.length > 20 ? item.title.substring(0, 20) + '...' : item.title)
                }
            };

            // Update chart series with both accepted and pending data
            this.chartSeries = [
                {
                    name: 'Accepted',
                    data: activitiesData.map(item => item.accepted_count || 0)
                },
                {
                    name: 'Pending',
                    data: activitiesData.map(item => item.pending_count || 0)
                }
            ];

            // Prepare pie chart data
            this.pieChartSeries = [
                this.analyticsData.acceptedParticipants || 0,
                this.analyticsData.pendingParticipants || 0,
                this.analyticsData.rejectedParticipants || 0
            ];

            // Set pieChartData to show the chart
            this.pieChartData = this.pieChartSeries.filter(value => value > 0);

            // Prepare top users chart data
            const topUsersData = this.analyticsData.topUsersByClubs || [];

            // Update top users chart options with categories
            this.topUsersChartOptions = {
                ...this.topUsersChartOptions,
                xaxis: {
                    ...this.topUsersChartOptions.xaxis,
                    categories: topUsersData.map(item => item.user_name.length > 15 ? item.user_name.substring(0, 15) + '...' : item.user_name)
                }
            };

            // Update top users chart series with data
            this.topUsersChartSeries = [{
                name: 'Clubs Created',
                data: topUsersData.map(item => item.clubs_count || 0)
            }];

            // Prepare top clubs by posts chart data
            const topClubsPostsData = this.analyticsData.topClubsByPosts || [];

            // Update top clubs posts chart options with categories
            this.topClubsPostsChartOptions = {
                ...this.topClubsPostsChartOptions,
                xaxis: {
                    ...this.topClubsPostsChartOptions.xaxis,
                    categories: topClubsPostsData.map(item => item.club_title.length > 15 ? item.club_title.substring(0, 15) + '...' : item.club_title)
                }
            };

            // Update top clubs posts chart series with data
            this.topClubsPostsChartSeries = [{
                name: 'Posts Created',
                data: topClubsPostsData.map(item => item.posts_count || 0)
            }];
        },
        getProfilePicture(user) {
            if (user && user.profile_picture_url) {
                return process.env.VUE_APP_PUBLIC_URL + user.profile_picture_url;
            }
            return this.getDefaultAvatar();
        },
        getDefaultAvatar() {
            return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNlNWU3ZWYiLz4KPHBhdGggZD0iTTIwIDEwQzIyLjA3NjEgMTAgMjQgMTIuMDIzOSAyNCAxNEMyNCAxNS45NzYxIDIyLjA3NjEgMTggMjAgMThDMTcuOTIzOSAxOCAxNiAxNS45NzYxIDE2IDE0QzE2IDEyLjAyMzkgMTcuOTIzOSAxMCAyMCAxMFoiIGZpbGw9IiM5Y2EzYWYiLz4KPHBhdGggZD0iTTI4IDI4QzI4IDI0LjY4NjMgMjQuNDE4MyAyMiAyMCAyMkMxNS41ODE3IDIyIDEyIDI0LjY4NjMgMTIgMjhIMjhaIiBmaWxsPSIjOWNhM2FmIi8+Cjwvc3ZnPgo=';
        },
        handleImageError(event) {
            // Handle profile picture loading error
            event.target.src = this.getDefaultAvatar();
        },
        getThumbnailUrl(thumbnailUrl) {
            if (thumbnailUrl) {
                return process.env.VUE_APP_PUBLIC_URL + thumbnailUrl;
            }
            return this.defaultThumbnail;
        },
        handleThumbnailError(event) {
            // Handle thumbnail loading error
            event.target.src = this.defaultThumbnail;
        },
        getFormThumbnailUrl() {
            if (this.form.thumbnail && typeof this.form.thumbnail === 'string') {
                return process.env.VUE_APP_PUBLIC_URL + this.form.thumbnail;
            }
            return this.defaultThumbnail;
        },
        handleFormThumbnailError(event) {
            // Handle form thumbnail loading error
            event.target.src = this.defaultThumbnail;
        },
        handleScroll(event) {
            const container = event.target;
            const scrollTop = container.scrollTop;
            const scrollHeight = container.scrollHeight;
            const clientHeight = container.clientHeight;

            // Calculate how close we are to the bottom (within 200px)
            const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

            // Trigger infinite loading when near bottom and we have more data
            if (distanceFromBottom < 200 && this.hasMore && !this.isLoading) {
                this.triggerInfiniteLoading();
            }
        },
        async triggerInfiniteLoading() {
            if (this.isLoading || !this.hasMore) {
                return;
            }

            console.log('Loading more activities...');
            this.page += 1;
            await this.fetchActivities();
        },
        async registerToActivity(item) {
            // Set loading state for this specific item
            this.registeringItems[item.token] = true;

            try {
                const result = await this.$apollo.mutate({
                    mutation: REGISTER_EXTRACURRICULAR_ACTIVITY,
                    variables: { token: item.token }
                });

                const response = result.data && result.data.registerExtracurricularActivity;

                if (response && response.error) {
                    throw new Error(response.message || 'Failed to register for club');
                }

                // Show success message
                this.$swal.fire({
                    title: 'Success!',
                    text: response.message || 'You have successfully registered for the club.',
                    icon: 'success',
                    timer: 3000,
                    showConfirmButton: false,
                    position: 'top-end',
                    toast: true
                });

                // Mark as pending (default status after registration)
                // Create a new array with updated participantStatus to avoid read-only issues
                const itemIndex = this.items.findIndex(activity => activity.token === item.token);
                if (itemIndex !== -1) {
                    // Create a new array with the updated item
                    this.items = this.items.map((activity, index) => {
                        if (index === itemIndex) {
                            return {
                                ...activity,
                                participantStatus: 'pending'
                            };
                        }
                        return activity;
                    });
                }
                this.registeringItems[item.token] = false;

                // Refresh analytics after registration
                this.fetchAnalytics();

            } catch (error) {
                console.error('Error registering for club:', error);
                this.$swal.fire({
                    title: 'Registration Failed',
                    text: error.message || 'Something went wrong. Please try again.',
                    icon: 'error',
                    position: 'top-end',
                    toast: true
                });
                this.registeringItems[item.token] = false;
            }
        },
        goToActivityDetail(token) {
            this.$router.push(`/student-life/${token}`);
        },
    },
};
</script>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-clamp: 2;
}

.line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-clamp: 3;
}

/* Custom scrollbar for modal */
.max-h-\[60vh\]::-webkit-scrollbar {
    width: 6px;
}

.max-h-\[60vh\]::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
}

.max-h-\[60vh\]::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.max-h-\[60vh\]::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

/* Smooth animations */
.group:hover .group-hover\:scale-105 {
    transform: scale(1.05);
}

/* File upload hover effect */
.hover\:border-blue-400:hover {
    border-color: #60a5fa;
}

.hover\:bg-blue-50:hover {
    background-color: #eff6ff;
}
</style>
